<template>
  <div class="bg-white rounded-xl shadow p-2 md:p-4 flex items-center justify-between md:max-w-md max-w-sm md:h-16 transition-all duration-150">
    <div>
      <Select v-model="selectedWeek" @update:model-value="handleWeekSelect">
        <SelectTrigger class="min-w-56 md:min-w-64 pl-3 shadow-none">
          <div class="flex items-center justify-between w-full md:text-lg font-normal text-black">
            <LoaderCircle v-if="curriculumStore.isLoading" class="h-4 w-4 animate-spin ml-2" />
            <SelectValue v-else :placeholder="props.currentWeek ? `Week ${props.currentWeek.current_week} (Current week)` : 'Select a week'" />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="week in previousWeeks" :key="week" :value="week.toString()">
            Week {{ week }}
          </SelectItem>
          <SelectItem 
            v-if="props.currentWeek" 
            :value="props.currentWeek.current_week.toString()"
            class="font-medium text-black"
            :disabled="props.restrictToPast"
          >
            Week {{ props.currentWeek.current_week }} (Current week)
          </SelectItem>
          <SelectItem v-for="week in nextWeeks" :key="week" :value="week.toString()">
            Week {{ week }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div class="flex items-center space-x-5 ml-5">
      <button
class="text-black p-2 rounded-full" 
        :disabled="isPrevDisabled || curriculumStore.isLoading"
        :class="{ 'opacity-30': isPrevDisabled || curriculumStore.isLoading, 'hover:bg-gray-100': !isPrevDisabled && !curriculumStore.isLoading }"
        @click="navigateToPreviousWeek"
      >
        <ChevronLeft class="w-4 h-4 md:w-5 md:h-5" />
      </button>
      <button
class="text-black p-2 rounded-full"
        :disabled="isNextDisabled || curriculumStore.isLoading"
        :class="{ 'opacity-30': isNextDisabled || curriculumStore.isLoading, 'hover:bg-gray-100': !isNextDisabled && !curriculumStore.isLoading }"
        @click="navigateToNextWeek"
      >
        <ChevronRight class="w-4 h-4 md:w-5 md:h-5" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { ChevronLeft, ChevronRight, LoaderCircle } from 'lucide-vue-next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCurriculumStore } from '@/stores/curriculum';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();

// Get the preview weeks from the current week data, fallback to 3 if not available
const previewWeeks = computed(() => {
  if (props.currentWeek?.preview_weeks) {
    return props.currentWeek?.preview_weeks - 1
  }
  return 3
});

const previousWeeks = computed(() => {
  // const weeks = Object.keys(props.curriculum.backlog);
  // return weeks

  const weeks: number[] = []
  for (let i = 1; i < Number(props.currentWeek?.current_week); i++) {
    weeks.push(i)
  }
  return weeks
});

const nextWeeks = computed(() => {
  const currentWeekNum = props.currentWeek?.current_week || 1;
  const totalWeeks = props.currentWeek?.total_weeks;

  if (props.restrictToPast) {
    return [] as number[];
  }

  // The number of future weeks to show is based on preview_weeks
  const maxPreviewWeek = currentWeekNum + previewWeeks.value;

  // The final week shown should not exceed total_weeks if it's available
  const lastWeekToShow = totalWeeks ? Math.min(maxPreviewWeek, totalWeeks) : maxPreviewWeek;

  const weeks: number[] = [];
  for (let i = currentWeekNum + 1; i <= lastWeekToShow; i++) {
    weeks.push(i);
  }
  return weeks;
});

interface CurrentWeekData {
  current_week: number;
  preview_weeks: number;
  total_weeks?: number;
  [key: string]: any;
}

const props = defineProps<{
  currentWeek: CurrentWeekData | null;
  curriculum: {
    backlog: Record<string, any>;
    future: Record<string, any>;
    [key: string]: any;
  };
  expeditionId: string | number | null;
  restrictToPast?: boolean;
  defaultToPrevious?: boolean;
}>();

const curriculumStore = useCurriculumStore();

const emit = defineEmits(['week-changed']);

// Initialize based on URL query parameter if available, otherwise use current week
const weekFromQuery = route.query.week ? String(route.query.week) : null;

// Always prefer query parameter over current week
const selectedWeek = ref(weekFromQuery || (props.currentWeek?.current_week?.toString() || ''));
const lastFetchedWeek = ref(''); // Start empty to ensure first fetch happens

// Watch for expeditionId to become available
watch(() => props.expeditionId, async (newExpeditionId) => {
  if (!newExpeditionId) return;
  
  // Only fetch if we haven't fetched anything yet
  if (lastFetchedWeek.value === '') {
    const weekFromQuery = route.query.week ? String(route.query.week) : null;
    
    if (weekFromQuery) {
      // First fetch the week data to validate it
      await curriculumStore.fetchProgressByWeek(weekFromQuery, String(newExpeditionId));
      lastFetchedWeek.value = weekFromQuery;
      
      // Validate the week against total_weeks if available
      if (curriculumStore.activeWeek?.total_weeks) {
        const requestedWeek = parseInt(weekFromQuery);
        const totalWeeks = curriculumStore.activeWeek.total_weeks;
        
        if (requestedWeek > totalWeeks) {
          console.warn(`⚠️ Requested week ${requestedWeek} exceeds total_weeks ${totalWeeks}, redirecting to current week`);
          // Fetch current week data and redirect to current week URL
          await curriculumStore.fetchCurrentWeekProgress(String(newExpeditionId));
          if (curriculumStore.activeWeek?.current_week) {
            const currentWeekNumber = curriculumStore.activeWeek.current_week;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ 
              query: { week: currentWeekNumber.toString() } 
            });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ 
              query: { week: '1' } 
            });
          }
          return;
        }
      }

      // Additional validation: restrict to past weeks only
      if (props.restrictToPast && curriculumStore.activeWeek?.current_week) {
        const requestedWeek = parseInt(weekFromQuery);
        const currentWeekNumber = curriculumStore.activeWeek.current_week;
        if (requestedWeek >= currentWeekNumber) {
          const prev = Math.max(1, currentWeekNumber - 1);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          router.replace({ query: { ...route.query, week: prev.toString() } });
          return;
        }
      }
    } else {
      // Fetch current week data first to get the current week number
      await curriculumStore.fetchCurrentWeekProgress(String(newExpeditionId));
      lastFetchedWeek.value = 'current';
      
      // Redirect based on mode
      if (props.defaultToPrevious && curriculumStore.activeWeek?.current_week) {
        const currentWeekNumber = curriculumStore.activeWeek.current_week;
        const prev = Math.max(1, currentWeekNumber - 1);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ 
          query: { ...route.query, week: prev.toString() } 
        });
      } else if (curriculumStore.activeWeek?.current_week) {
        const currentWeekNumber = curriculumStore.activeWeek.current_week;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ 
          query: { ...route.query, week: currentWeekNumber.toString() } 
        });
      } else {
        // Fallback: if current_week is not available, default to week 1
        console.warn('⚠️ Current week not available, defaulting to week 1');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ 
          query: { ...route.query, week: '1' } 
        });
      }
    }
  }
}, { immediate: true });

// If we have a week from query params, fetch it on mount
onMounted(async () => {
  // This is now handled by the expeditionId watcher above
});

const handleWeekSelect = async (weekValue: any) => {
  if (!weekValue) return;
  const weekString = String(weekValue);
  if (weekString === lastFetchedWeek.value) {
    return;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: { ...route.query, week: weekString } });
  } catch (error) {
    console.error('Error updating week:', error);
  }
};

// Watch for URL parameter changes - simplified since we always redirect to include week
watch(() => route.query.week, async (newWeek) => {
  if (!props.expeditionId) {
    console.warn('No expedition ID provided, skipping week fetch');
    return;
  }
  
  // Don't interfere with initial fetch from expeditionId watcher
  if (lastFetchedWeek.value === '') {
    return;
  }

  if (newWeek) {
    const weekString = String(newWeek);
    if (weekString !== lastFetchedWeek.value) {
      await curriculumStore.fetchProgressByWeek(weekString, String(props.expeditionId));
      lastFetchedWeek.value = weekString;
      selectedWeek.value = weekString;
      
      // Validate the week against total_weeks if available
      if (curriculumStore.activeWeek?.total_weeks) {
        const requestedWeek = parseInt(weekString);
        const totalWeeks = curriculumStore.activeWeek.total_weeks;
        
        if (requestedWeek > totalWeeks) {
          console.warn(`⚠️ Requested week ${requestedWeek} exceeds total_weeks ${totalWeeks}, redirecting to current week`);
          // Fetch current week data and redirect to current week URL
          await curriculumStore.fetchCurrentWeekProgress(String(props.expeditionId));
          if (curriculumStore.activeWeek?.current_week) {
            const currentWeekNumber = curriculumStore.activeWeek.current_week;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ 
              query: { week: currentWeekNumber.toString() } 
            });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ 
              query: { week: '1' } 
            });
          }
          return;
        }
      }

      // Additional validation: restrict to past weeks only
      if (props.restrictToPast && curriculumStore.activeWeek?.current_week) {
        const requestedWeek = parseInt(weekString);
        const currentWeekNumber = curriculumStore.activeWeek.current_week;
        if (requestedWeek >= currentWeekNumber) {
          const prev = Math.max(1, currentWeekNumber - 1);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          router.replace({ query: { ...route.query, week: prev.toString() } });
          return;
        }
      }
    } else if (selectedWeek.value !== weekString) {
      selectedWeek.value = weekString;
    }
  }
});

// Check if navigation is possible
const isPrevDisabled = computed(() => {
  const selectedWeekNum = parseInt(selectedWeek.value) || props.currentWeek?.current_week || 1;
  return selectedWeekNum <= 1; // Disabled if we're at week 1 or less
});

const isNextDisabled = computed(() => {
  const currentWeekNum = props.currentWeek?.current_week || 1;
  const selectedWeekNum = parseInt(selectedWeek.value) || currentWeekNum;
  const totalWeeks = props.currentWeek?.total_weeks;

  if (props.restrictToPast) {
    const lastPastWeek = Math.max(1, currentWeekNum - 1);
    return selectedWeekNum >= lastPastWeek;
  }

  // The number of future weeks to show is based on preview_weeks
  const maxPreviewWeek = currentWeekNum + previewWeeks.value;

  // The final week shown should not exceed total_weeks if it's available
  const lastWeekToShow = totalWeeks ? Math.min(maxPreviewWeek, totalWeeks) : maxPreviewWeek;
  
  return selectedWeekNum >= lastWeekToShow;
});

const navigateToPreviousWeek = () => {
  if (isPrevDisabled.value) return;
  
  const currentWeekNum = parseInt(selectedWeek.value) || props.currentWeek?.current_week || 1;
  const prevWeek = currentWeekNum - 1;
  console.log('Navigating to previous week:', prevWeek);
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace({ query: { ...route.query, week: prevWeek.toString() } });
};

const navigateToNextWeek = () => {
  if (isNextDisabled.value) return;
  
  const currentWeekNum = parseInt(selectedWeek.value) || props.currentWeek?.current_week || 1;
  const nextWeek = currentWeekNum + 1;
  console.log('Navigating to next week:', nextWeek);
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace({ query: { ...route.query, week: nextWeek.toString() } });
};
</script>

<style scoped>
</style> 